import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { WalletButton } from './WalletButton';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAppKitAccount } from '@reown/appkit/react';
import { Link, useLocation } from 'react-router-dom';

const links = [
  { id: 'home', text: 'Home' },
  { id: 'slider', text: 'Story' },
  { id: 'staking', text: 'Staking' },
  { id: 'tokenomics', text: 'Tokenomics' },
  { id: 'roadmap', text: 'Roadmap' },
  { id: 'referral', text: 'Referral' },
  { id: 'airdrop', text: 'Airdrop' },
  { id: 'faq', text: 'FAQ' },
  { id: 'whitepaper', text: 'Whitepaper', href: '/whitepaper.pdf', external: true }
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { connected: isSolanaConnected } = useWallet();
  const { isConnected: isEvmConnected } = useAppKitAccount();
  const location = useLocation();
  
  // Check if we're on the main page or a different route
  const isMainPage = location.pathname === '/';
  
  // This auto wallet button will show whichever wallet is connected
  // and let users disconnect that specific wallet
  
  const handleNavigation = (id: string, href?: string) => {
    if (href) {
      if (href.startsWith('http') || href.endsWith('.pdf')) {
        // For external links
        window.open(href, '_blank');
      }
      setIsOpen(false);
      return;
    }
    
    // For main page sections
    if (isMainPage) {
      // Handle anchor navigation on main page
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on a different route, navigate to main page then to the section
      window.location.href = `/#${id}`;
    }
    
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-quantum-dark/80 backdrop-blur-lg border-b border-white/10 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 mr-4">
            <Link 
              to="/"
              className="text-2xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent"
            >
              Quantum Genesis
            </Link>
          </div>
          
          {/* Navigation Links */}
          <div className="hidden md:flex flex-1 justify-center">
            <div className="flex items-baseline space-x-5">
              {links.map((link) => {
                if (link.external) {
                  return (
                    <a
                      key={link.id}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium px-3 py-2 text-quantum-light hover:text-quantum-primary transition-colors duration-200"
                    >
                      {link.text}
                    </a>
                  );
                } else {
                  return (
                    <Link
                      key={link.id}
                      to={isMainPage ? `#${link.id}` : `/#${link.id}`}
                      className="text-sm font-medium px-3 py-2 text-quantum-light hover:text-quantum-primary transition-colors duration-200"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavigation(link.id);
                      }}
                    >
                      {link.text}
                    </Link>
                  );
                }
              })}
            </div>
          </div>
          
          {/* Right Side Controls - Fixed Width */}
          <div className="hidden md:flex items-center space-x-4 min-w-[150px] justify-end">
            {/* Auto-detect wallet type */}
            <div className="flex-shrink-0">
              <WalletButton network="auto" />
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden ml-auto">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-quantum-light hover:text-quantum-primary focus:outline-none"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute w-full bg-quantum-dark/95 backdrop-blur-lg border-b border-white/10">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {links.map((link) => {
              if (link.external) {
                return (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-quantum-light hover:text-quantum-primary transition-colors duration-200 px-3 py-2 text-base font-medium w-full text-left"
                  >
                    {link.text}
                  </a>
                );
              } else {
                return (
                  <Link
                    key={link.id}
                    to={isMainPage ? `#${link.id}` : `/#${link.id}`}
                    className="block text-quantum-light hover:text-quantum-primary transition-colors duration-200 px-3 py-2 text-base font-medium w-full text-left"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigation(link.id);
                    }}
                  >
                    {link.text}
                  </Link>
                );
              }
            })}
            <div className="px-3 py-2 flex justify-end items-center">
              {/* Auto-detect wallet type */}
              <WalletButton network="auto" className="justify-center" />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}